// third-party
import { FormattedMessage } from "react-intl";

// assets
import {
  IconApps,
  IconCalendarEvent,
  IconChartArcs,
  IconClipboardCheck,
  IconDashboard,
  IconDatabaseExport,
  IconDeviceAnalytics,
  IconForms,
  IconGiftCard,
  IconPictureInPicture,
  IconUsers,
  IconUserX,
  IconVideo,
} from "@tabler/icons";
import { BiSolidBookAdd } from "react-icons/bi";
// constant
const icons = {
  IconDashboard,
  IconDeviceAnalytics,
  IconClipboardCheck,
  IconPictureInPicture,
  IconForms,
  IconChartArcs,
  IconDatabaseExport,
  IconApps,
  IconUsers,
  IconUserX,
  IconCalendarEvent,
  IconVideo,
  IconGiftCard,
  BiSolidBookAdd,
};

// ==============================|| DASHBOARD MENU ITEMS ||============================== //

/*
 * The admin tree, as a function of the role.
 *
 * It used to be a constant built when this module first loaded, reading
 * localStorage at import time. That froze it at whatever role the tab happened
 * to start with, and only the JWT sign-in re-imports the bundle afterwards —
 * it ends in a hard `window.location.href` reload. The Google sign-in writes
 * `users` and then navigates client-side with no reload, so that user kept the
 * role-0 tree until they refreshed by hand: no Help Management, no Cloud, no
 * Invoice Management. Harmless while it only cost a few dropdown rows; not
 * harmless now that the sidebar mirrors this same tree, where it would take out
 * most of a nav section.
 *
 * Taking the role as an argument fixes that, and it is also what makes the tree
 * buildable for a role the tab is not currently in — which is how it is tested.
 *
 * Memoised per role, so the array keeps its identity between calls. That
 * identity is load-bearing: the sidebar caches its admin group on it, and
 * AdminMenu's useMemo and its redirect effect both depend on it. A fresh array
 * per call would re-run the redirect effect on every render.
 */
const treeCache = new Map();

export const buildAdminTree = (roleId = 0) => {
  if (treeCache.has(roleId)) return treeCache.get(roleId);

  const children = [
    ...(roleId === 1
      ? [
          {
            id: "Help Management",
            title: <FormattedMessage id="Help Management" />,
            type: "item",
            url: "/Help_Management",
            icon: icons.IconClipboardCheck,
            breadcrumbs: false,
          },
        ]
      : roleId >= 2
        ? [
            {
              id: "Help",
              title: <FormattedMessage id="Help Management" />,
              type: "collapse",
              icon: icons.IconApps,
              breadcrumbs: false,
              children: [
                {
                  id: "Help Management",
                  title: <FormattedMessage id="Help Tickets" />,
                  type: "item",
                  url: "/Help_Management",
                  icon: icons.IconClipboardCheck,
                  breadcrumbs: false,
                },
                {
                  id: "Help Status",
                  title: <FormattedMessage id="Help Status" />,
                  type: "item",
                  url: "/Help_Status",
                  icon: icons.IconChartArcs,
                  breadcrumbs: false,
                },
                {
                  id: "Feature Requests",
                  title: <FormattedMessage id="Feature Requests" />,
                  type: "item",
                  url: "/Help_Feature_Requests",
                  icon: icons.IconClipboardCheck,
                  breadcrumbs: false,
                },
                // ===========================================================
                // AI TICKET RESOLUTION DISABLED
                // The "AI Help Desk" admin menu entry is commented out, so the
                // AI-only screen is unreachable (its route is also commented
                // out in routes/MainRoutes.js). Help Tickets, Help Status and
                // Feature Requests above are unaffected.
                // ===========================================================
                // {
                //   id: "AIHelpDeck",
                //   title: <FormattedMessage id="AI Help Desk" />,
                //   type: "item",
                //   url: "/AIHelpDeck",
                //   icon: icons.IconClipboardCheck,
                //   breadcrumbs: false,
                // },
              ],
            },
          ]
        : []),
      {
      id: "Manage users",
      title: <FormattedMessage id="Manage users" />,
      type: "item",
      url: "/User_Management",
      icon: icons.IconForms,
      breadcrumbs: false,
    },
    {
      id: "Onboarding Management",
      title: <FormattedMessage id="Onboarding" />,
      type: "item",
      url: "/Onboarding_Management",
      icon: icons.IconClipboardCheck,
      breadcrumbs: false,
    },
    {
      id: "Event Management",
      title: <FormattedMessage id="Event Management" />,
      type: "item",
      url: "/Event_Management",
      icon: icons.IconPictureInPicture,
      breadcrumbs: false,
    },
    {
      id: "Research Report Admin",
      title: <FormattedMessage id="Research Report Admin" />,
      type: "item",
      url: "/Admin/ResearchReport",
      icon: icons.IconCalendarEvent,
      breadcrumbs: false,
      isbeta: false,
    },
    {
        id: 'Meetup Event',
        title: <FormattedMessage id="Meetup Event" />,
        type: 'item',
        url: '/meetupadmin',
        icon: icons.IconPictureInPicture,
        breadcrumbs: false
    },
    {
      id: "Core Watchlist",
      title: <FormattedMessage id="Core Watchlist" />,
      type: "item",
      url: "/Core_Watchlist",
      icon: icons.IconChartArcs,
      breadcrumbs: false,
    },
     {
      id: "My List Admin",
      title: <FormattedMessage id="Watchlist" />,
      type: "item",
      url: "/Admin/MyList",
      icon: icons.IconClipboardCheck,
      breadcrumbs: false,
    },
    {
      id: "Fundamental Analysis",
      title: <FormattedMessage id="Fundamental Analysis" />,
      type: "item",
      url: "/Admin/FA",
      icon: icons.IconForms,
      breadcrumbs: false,
    },
    {
      id: "TF Masters Admin",
      title: <FormattedMessage id="TF Masters" />,
      type: "item",
      url: "/Admin/TFMasters",
      icon: icons.IconClipboardCheck,
      breadcrumbs: false,
    },
    {
      id: "TF Pulse Admin",
      title: <FormattedMessage id="TF Pulse Admin" />,
      type: "item",
      url: "/Admin/TFPulse",
      icon: icons.IconDeviceAnalytics,
      breadcrumbs: false,
    },
    {
      id: "WellnessEvent",
      title: <FormattedMessage id="Wellness Event" />,
      type: "item",
      url: "/WellnessEvent",
      icon: icons.IconPictureInPicture,
      breadcrumbs: false,
    },

    {
      id: "AddBooks",
      title: <FormattedMessage id="Book Club" />,
      type: "item",
      url: "/BookClubAdmin",
      icon: icons.BiSolidBookAdd,
      breadcrumbs: false,
    },
    {
      id: "University",
      title: <FormattedMessage id="University" />,
      type: "item",
      url: "/University",
      icon: icons.BiSolidBookAdd,
      breadcrumbs: false,
    },
    {
      id: "ReferenceAdmin",
      title: <FormattedMessage id="Reference" />,
      type: "item",
      url: "/ReferenceAdmin",
      icon: icons.IconClipboardCheck,
      breadcrumbs: false,
    },
    {
      id: "qic",
      title: <FormattedMessage id="QIC" />,
      type: "collapse",
      url: "/User_Management",
      icon: icons.IconApps,
      breadcrumbs: false,
      children: [ 
        {
          id: "Qicusermanagement",
          title: <FormattedMessage id="User Management" />,
          type: "collapse",
          icon: icons.IconUsers,
          url: "/qic_admin",
          breadcrumbs: false,
          children: [
            {
              id: "createTeam",
              title: <FormattedMessage id="Create Team" />,
              type: "item",
              url: "/qic_admin/createteam",
              breadcrumbs: false,
            },
            {
              id: "assignteammember",
              title: <FormattedMessage id="Assign Team Members" />,
              type: "item",
              url: "/Admin/Qic/User",
              breadcrumbs: false,
            },
            {
              id: "updateteamstatus",
              title: <FormattedMessage id="Stage Shortlist" />,
              type: "item",
              url: "/Admin/Qic/Team",
              breadcrumbs: false,
            },
            {
              id: "winnershortlist",
              title: <FormattedMessage id="Winner Shortlist" />,
              type: "item",
              url: "/Admin/Qic/WinnerShortlist",
              breadcrumbs: false,
            },
          ],
        },
        {
          id: "QicEvent",
          title: <FormattedMessage id=" Event Management" />,
          type: "collapse",
          icon: icons.IconCalendarEvent,
          url: "/qic_event",
          breadcrumbs: false,
          children: [
            {
              id: "createEvent",
              title: <FormattedMessage id="Create Event" />,
              type: "item",
              url: "/qic_event",
              breadcrumbs: false,
              children: [],
            },
            {
              id: "assignstages",
              title: <FormattedMessage id="Assign Stages" />,
              type: "item",
              url: "/qic_event/stages",
              breadcrumbs: false,
            },
            {
              id: "businessrankingqic",
              title: <FormattedMessage id="Setting QIC Votes" />,
              type: "item",
              url: "/qic/setting_qicvotes",
              breadcrumbs: false,
            },
          ],
        },
      ],
    },
     {
      id: "portal_walkthrough_links",
      title: <FormattedMessage id="Dhruva walkthrough links" />,
      type: "item",
      url: "/demo_video_link",
      icon: icons.IconVideo,
      breadcrumbs: false,
    },

    {
      id: "eod download",
      title: <FormattedMessage id="EOD" />,
      type: "item",
      url: "/eod_download",
      icon: icons.IconClipboardCheck,
      breadcrumbs: false,
    },
    {
      id: "Report",
      title: <FormattedMessage id="Report" />,
      type: "item",
      url: "/UtilityReport",
      icon: icons.IconForms,
      breadcrumbs: false,
    },
    ...(roleId === 2
      ? [
          {
            id: "Cloud",
            title: <FormattedMessage id="Cloud" />,
            type: "collapse",
            icon: icons.IconCalendarEvent,
            url: "/qic_event",
            breadcrumbs: false,
            children: [
              {
                id: "EC2 Type Change",
                title: <FormattedMessage id="EC2 Type Change" />,
                type: "item",
                url: "/ec2_type_change",
                breadcrumbs: false,
                children: [],
              },
              {
                id: "Cron Dashboard",
                title: <FormattedMessage id="Cron Dashboard" />,
                type: "item",
                url: "/cron_dashboard",
                breadcrumbs: false,
              },
            ],
          },
        ]
      : []),
      ...(roleId == 2 ? [
        {
          id: "Invoice Management",
          title: <FormattedMessage id="Invoice Management" />,
          type: "item",
          url: "/invoicemanagementdashboard",
          icon: icons.IconForms,
          breadcrumbs: false,
        },
        {
          id: "User Report",
          title: <FormattedMessage id="User Report" />,
          type: "item",
          url: "/userreportdashboard",
          icon: icons.IconUsers,
          breadcrumbs: false,
        },
      ] : []),
  ];

  treeCache.set(roleId, children);
  return children;
};

/*
 * The role the tab is currently signed in as, read at CALL time.
 *
 * The try/catch is the old module-scope guard, unchanged: `users` is written by
 * several sign-in paths and a half-written or absent value must not throw.
 */
const roleOf = () => {
  try {
    return Number(JSON.parse(window.localStorage.getItem("users"))?.role_id) || 0;
  } catch (error) {
    return 0;
  }
};

/*
 * The sidebar-shaped group, memoised per role for the same identity reason as
 * the tree above.
 *
 * The id, title and icon are unchanged from when this was the `Admin` constant;
 * only the children are now built rather than frozen.
 */
const adminCache = new Map();

export const getAdmin = () => {
  const roleId = roleOf();
  if (!adminCache.has(roleId)) {
    adminCache.set(roleId, {
      id: "Admin Dashboard",
      title: <FormattedMessage id="Admin Dashboard" />,
      icon: icons.IconDashboard,
      type: "group",
      children: buildAdminTree(roleId),
    });
  }
  return adminCache.get(roleId);
};

export default getAdmin;
